// Client-side validation, mirrored from the server's zod schemas
// (packages/server/src/schemas.ts customerSchema). Same rules so the form
// catches problems before the round-trip; the server still re-validates.
// Implements: architecture.md v1.0 section 5.

import { z } from 'zod';

export const customerFormSchema = z.object({
  name: z.string().trim().min(1, 'Please enter your name').max(200, 'Name is too long'),
  email: z
    .string()
    .trim()
    .min(1, 'Please enter your email')
    .email('Enter a valid email address')
    .max(320, 'Email is too long'),
  note: z.string().trim().max(2000, 'Note is too long (2000 characters max)').optional(),
});

export type CustomerForm = z.infer<typeof customerFormSchema>;

export type FieldErrors = Partial<Record<keyof CustomerForm, string>>;

/** Validate the details form; returns either typed data or per-field errors. */
export function validateCustomer(
  input: { name: string; email: string; note: string },
): { ok: true; data: CustomerForm } | { ok: false; errors: FieldErrors } {
  const result = customerFormSchema.safeParse({
    name: input.name,
    email: input.email,
    // Treat an empty note as absent (it is optional on the server).
    note: input.note.trim() === '' ? undefined : input.note,
  });
  if (result.success) return { ok: true, data: result.data };
  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0] as keyof CustomerForm | undefined;
    if (key && !errors[key]) errors[key] = issue.message;
  }
  return { ok: false, errors };
}

// ---- Contact form ("message Numaco"), mirrored from the server's contactSchema
// (packages/server/src/schemas.ts): name 1..100, email valid and <=200, message
// 1..4000. Same rules so the form catches problems before the round-trip; the
// server still re-validates. The honeypot `website` field is not part of this:
// it is appended at submit time and handled server-side.

export const contactFormSchema = z.object({
  name: z.string().trim().min(1, 'Please enter your name').max(100, 'Name is too long'),
  email: z
    .string()
    .trim()
    .min(1, 'Please enter your email')
    .email('Enter a valid email address')
    .max(200, 'Email is too long'),
  message: z
    .string()
    .trim()
    .min(1, 'Please enter a message')
    .max(4000, 'Message is too long (4000 characters max)'),
});

export type ContactForm = z.infer<typeof contactFormSchema>;

export type ContactFieldErrors = Partial<Record<keyof ContactForm, string>>;

/** Validate the contact form; returns either typed data or per-field errors. */
export function validateContact(
  input: { name: string; email: string; message: string },
): { ok: true; data: ContactForm } | { ok: false; errors: ContactFieldErrors } {
  const result = contactFormSchema.safeParse(input);
  if (result.success) return { ok: true, data: result.data };
  const errors: ContactFieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0] as keyof ContactForm | undefined;
    if (key && !errors[key]) errors[key] = issue.message;
  }
  return { ok: false, errors };
}
